import {
  disableCaptionsTracking,
  enableCaptionsTracking,
  getLatestCaption,
  getWorkbookData,
} from "api/api_client";
import { createErrorToast } from "components/shared/toasts";
import { ForumAPI } from "contexts/forum_api_context";
import {
  addBreakout,
  addResource,
  createNewPoll,
  endPollSession,
  setBreakoutRooms,
  setResources,
  startBreakoutSession,
  startPollSession,
  visitBreakoutRoom,
} from "shared/reducers/classroom";
import {
  BreakoutRoomType,
  BreakoutSessionType,
  BreakoutType,
  MinervaResourceSubtypeType,
  PollChoiceType,
  PollGradingTypeType,
  PollResponseType,
  PollSessionStatusType,
  PollSessionType,
  PollType,
  PollTypeType,
  ResourceType,
  ResourceTypeType,
} from "shared/types";
import { RoleType, UserType } from "shared/types/user";
import {
  WorkbookComponentType,
  WorkbookComponentTypeType,
  WorkbookQuestionMultipleChoiceDataType,
  WorkbookQuestionTypeType,
} from "shared/types/workbook";
import { AppDispatch } from "store";
import { setCurrentlyBackingUpPoll } from "store/reducers/local";
import { setQuestion, setWorkbook } from "store/reducers/workbook";

const MAX_UPLOAD_FILE_SIZE_MB = 50; // determined by "client_max_body_size" parameter in nginx conf

interface BreakoutResponseType {
  breakout: {
    id: string;
    duration_in_seconds: number;
    grouping: string;
    title: string;
    has_flavors: boolean;
    share_resource_between_groups: boolean;
    breakout_rooms: BreakoutRoomType[];
  };
  breakout_session: BreakoutSessionType;
  resources: ResourceType[];
}

function _pickRandomRoom(rooms: BreakoutRoomType[]) {
  return rooms[Math.floor(Math.random() * rooms.length)];
}

function _startBreakoutSession(
  responseJson: BreakoutResponseType,
  users: UserType[],
  presentUserIds: string[],
  dispatch: AppDispatch,
) {
  const breakoutRooms = responseJson.breakout.breakout_rooms;
  const shouldDeletePreviousRooms =
    responseJson.breakout.grouping.startsWith("workbook-assignment");
  dispatch(setBreakoutRooms({ breakoutRooms, shouldDeletePreviousRooms }));

  // Move captioners and recording agent to breakout room
  const captioners = users.filter((user) => user.role === RoleType.Captioner);
  const userIdsToMoveToRandomBreakoutRoom = captioners.map(({ id }) => id);

  const recordingAgent = users.find((user) => user.is_recording_agent);
  if (recordingAgent) {
    userIdsToMoveToRandomBreakoutRoom.push(recordingAgent.id);
  }
  if (userIdsToMoveToRandomBreakoutRoom.length > 0) {
    const breakoutRoomsWithPresentUsers = breakoutRooms.filter((breakoutRoom) =>
      breakoutRoom.assigned_user_ids.some((userId) => presentUserIds.includes(userId)),
    );
    const breakoutRoomsWithPresentUsersRequestingCaptions = breakoutRooms.filter((breakoutRoom) =>
      breakoutRoom.assigned_user_ids.some((userId) => {
        if (!presentUserIds.includes(userId)) {
          return false;
        }
        const user = users.find(({ id }) => id === userId);
        if (!user) {
          return false;
        }
        return user.enable_live_captions;
      }),
    );

    let randomBreakoutRoom = breakoutRooms[0];
    if (breakoutRoomsWithPresentUsersRequestingCaptions.length > 0) {
      randomBreakoutRoom = _pickRandomRoom(breakoutRoomsWithPresentUsersRequestingCaptions);
    } else if (breakoutRoomsWithPresentUsers.length > 0) {
      randomBreakoutRoom = _pickRandomRoom(breakoutRoomsWithPresentUsers);
    }

    for (const userId of userIdsToMoveToRandomBreakoutRoom) {
      dispatch(visitBreakoutRoom({ userId, breakoutRoomId: randomBreakoutRoom?.id }));
    }
  }

  dispatch(setResources({ resources: responseJson.resources }));

  dispatch(
    startBreakoutSession({
      id: responseJson.breakout_session.id,
      breakoutId: responseJson.breakout_session.breakout_id,
      startedOn: responseJson.breakout_session.started_on,
      endedOn: responseJson.breakout_session.ended_on,
    }),
  );
}

export async function createBreakoutHelper(
  classId: string,
  breakouts: BreakoutType[],
  grouping: string,
  duration: number | string,
  users: UserType[],
  currentlyBackingUpPoll: string | null,
  serverMilliseconds: number,
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
) {
  const start = new Date(serverMilliseconds);
  let end = null;
  if (typeof duration === "number") {
    end = new Date(serverMilliseconds + duration * 1000);
  }
  const presentUserIds = users.filter((user) => user.is_present).map((user) => user.id);

  try {
    const response = await forumAPI.createBreakout(
      classId,
      `Breakout ${breakouts.length + 1}`,
      grouping,
      typeof duration === "number" ? duration : undefined,
      start,
      end,
      presentUserIds,
    );
    const responseJson = (await response.json()) as BreakoutResponseType;

    if (response.ok) {
      dispatch(
        addBreakout({
          id: responseJson.breakout.id,
          durationInSeconds: responseJson.breakout.duration_in_seconds,
          grouping: responseJson.breakout.grouping,
          title: responseJson.breakout.title,
          hasFlavors: responseJson.breakout.has_flavors,
          shareResourceBetweenGroups: responseJson.breakout.share_resource_between_groups,
        }),
      );
      _startBreakoutSession(responseJson, users, presentUserIds, dispatch);
      return responseJson;
    }

    if (response.status === 409 && !currentlyBackingUpPoll) {
      createErrorToast(
        "Error Starting Breakout",
        "End the corresponding poll and then try starting the breakout again.",
      );
    }

    window.flightLogger.error(
      `[createBreakoutHelper] createBreakout failed with status=${response.status}`,
    );
    throw new Error(JSON.stringify(responseJson));
  } catch (e) {
    window.flightLogger.error("[createBreakoutHelper] createBreakout failed");
    throw e;
  }
}

export async function createBreakoutSessionHelper(
  breakout: BreakoutType,
  users: UserType[],
  currentlyBackingUpPoll: string | null,
  serverMilliseconds: number,
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
) {
  const start = new Date(serverMilliseconds);
  let end: Date | null = null;
  if (breakout.duration_in_seconds) {
    end = new Date(serverMilliseconds + breakout.duration_in_seconds * 1000);
  }
  const presentUserIds = users.filter((user) => user.is_present).map((user) => user.id);

  try {
    const response = await forumAPI.createBreakoutSession(breakout.id, start, end, presentUserIds);
    const responseJson = (await response.json()) as BreakoutResponseType;

    if (response.ok) {
      _startBreakoutSession(responseJson, users, presentUserIds, dispatch);
      return responseJson;
    }

    if (response.status === 409 && !currentlyBackingUpPoll) {
      createErrorToast(
        "Error Starting Breakout",
        "End the corresponding poll and then try starting the breakout again.",
      );
    }

    window.flightLogger.error(
      `[createBreakoutSessionHelper] createBreakoutSession failed with status=${response.status}`,
    );
    throw new Error(JSON.stringify(responseJson));
  } catch (e) {
    window.flightLogger.error("[createBreakoutSessionHelper] createBreakoutSession failed");
    throw e;
  }
}

export async function createPollHelper(
  classId: string,
  question: string,
  type: PollTypeType,
  studentVisible: boolean,
  multipleResponse: boolean,
  pollChoices: PollChoiceType[],
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
) {
  const MAX_TITLE_LENGTH = 50;
  const title = question.slice(0, MAX_TITLE_LENGTH);
  try {
    const response = await forumAPI.createPoll(
      question,
      title,
      type,
      studentVisible,
      multipleResponse,
      pollChoices,
      type === PollTypeType.LongForm ? PollGradingTypeType.Outcome : PollGradingTypeType.None,
    );
    const responseJson = (await response.json()) as PollType;

    if (response.ok) {
      dispatch(createNewPoll(responseJson));

      // Create class poll (only used by Forum)
      await forumAPI.createClassPoll(classId, responseJson.id);

      return responseJson;
    }

    window.flightLogger.error(
      `[createPollHelper] createPoll failed with status=${response.status}`,
    );
    throw new Error(JSON.stringify(responseJson));
  } catch (e) {
    window.flightLogger.error("[createPoll] createPoll failed");
    throw e;
  }
}

export async function createPollSessionHelper(
  pollId: string,
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
) {
  try {
    const response = await forumAPI.createPollSession(pollId);
    const responseJson = (await response.json()) as PollSessionType;

    if (response.ok) {
      dispatch(startPollSession({ pollId, id: responseJson.id }));
      return responseJson;
    }

    window.flightLogger.error(
      `[createPollSessionHelper] createPollSession failed with status=${response.status}`,
    );
    throw new Error(JSON.stringify(responseJson));
  } catch (e) {
    window.flightLogger.error("[createPollSession] createPollSession failed");
    throw e;
  }
}

export async function endPollSessionHelper(
  pollSession: PollSessionType,
  pollResponses: PollResponseType[],
  pollType: PollTypeType,
  serverMilliseconds: number,
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
) {
  if (pollType === PollTypeType.LongForm) {
    dispatch(
      endPollSession({
        id: pollSession.id,
        endedAt: serverMilliseconds,
      }),
    );
    return;
  }
  dispatch(setCurrentlyBackingUpPoll({ pollId: pollSession.poll_id }));
  dispatch(
    endPollSession({
      id: pollSession.id,
      endedAt: serverMilliseconds,
    }),
  );
  const updatedPollSession = {
    ...pollSession,
    ended_at: serverMilliseconds,
    status: PollSessionStatusType.Completed,
  };

  await forumAPI.importPollSession(updatedPollSession, pollResponses);
  dispatch(setCurrentlyBackingUpPoll({ pollId: null }));
}

export async function createResourceHelper(
  classId: string,
  type: ResourceTypeType,
  dispatch: AppDispatch,
  forumAPI: ForumAPI,
  options: {
    file?: File;
    minervaResourceSubtype?: MinervaResourceSubtypeType;
    url?: string;
    titlePrefix?: string;
    precreated?: boolean;
  },
) {
  let title = "New Resource";

  if (type === ResourceTypeType.File) {
    title = options.file ? options.file.name : title;
  } else if (
    type === ResourceTypeType.ForumDoc &&
    options.minervaResourceSubtype === MinervaResourceSubtypeType.ForumDoc
  ) {
    title = "Forum Doc";
  } else if (
    type === ResourceTypeType.ForumDoc &&
    options.minervaResourceSubtype === MinervaResourceSubtypeType.ForumCodeDoc
  ) {
    title = "Forum Code Doc";
  } else if (
    type === ResourceTypeType.ForumDoc &&
    options.minervaResourceSubtype === MinervaResourceSubtypeType.CaptionsDoc
  ) {
    title = "Live Captions Doc";
  } else if ((type === ResourceTypeType.Url || type === ResourceTypeType.Video) && options.url) {
    title = options.url;
  } else if (type === ResourceTypeType.Whiteboard) {
    title = "Whiteboard";
  } else if (type === ResourceTypeType.Screenshare) {
    // Screenshare resources are not saved to the database
    return;
  }

  try {
    const response = await forumAPI.createResource({
      klass_id: classId,
      title: options.titlePrefix ? `${options.titlePrefix} - ${title}` : title,
      type,
      file: options.file,
      subtype: options.minervaResourceSubtype,
      url: options.url,
      is_public: true,
      precreated: options.precreated || false,
    });
    const responseJson = (await response.json()) as ResourceType;

    if (response.ok) {
      dispatch(addResource({ resource: responseJson }));
      return responseJson;
    }

    window.flightLogger.error(
      `[createResourceHelper] createResource failed with status=${response.status}`,
    );

    if (type === ResourceTypeType.File) {
      const errorTitle = "Error Uploading Resource";
      if (response.status === 413) {
        createErrorToast(errorTitle, `${title} exceeds the ${MAX_UPLOAD_FILE_SIZE_MB} MB limit.`);
      } else if (response.status === 422) {
        createErrorToast(errorTitle, JSON.stringify(responseJson));
      } else if (response.status === 504) {
        createErrorToast(
          errorTitle,
          `Uploading ${title} timed out. This may be due to the file being too large.`,
        );
      } else {
        createErrorToast(
          errorTitle,
          `There was an error while uploading ${title}. Please try again.`,
        );
      }
      return;
    }

    if (type === ResourceTypeType.Url) {
      const errorTitle = "Error Adding URL";
      if (response.status === 403) {
        createErrorToast(
          errorTitle,
          "Access to this Google document is restricted. Please change the permissions and try again.",
        );
      } else {
        createErrorToast(errorTitle, "There was an error while saving the URL. Please try again.");
      }
      return;
    }

    // Errors for all other resource types are handled below
    throw new Error(JSON.stringify(responseJson));
  } catch (e) {
    window.flightLogger.error("[createResourceHelper] createResource failed");
    createErrorToast("Error Creating Resource", "Something went wrong. Please try again.");
    throw e;
  }
}

export async function getWorkbookDataHelper(
  resourceUuid: string,
  resourceUuidToBreakoutRoomIdMap: Map<string, string>,
  breakoutId: string,
  classId: string,
  orgId: number,
  shareResourceBetweenGroups: boolean,
  flavor: string,
  dispatch: AppDispatch,
) {
  try {
    const sessionId = shareResourceBetweenGroups ? classId : breakoutId;
    const response = await getWorkbookData(resourceUuid, sessionId, orgId);
    const responseJson = await response.json();

    if (!response.ok) {
      window.flightLogger.error(
        `[getWorkbookDataHelper] getWorkbookData failed with status=${response.status}`,
      );
      throw new Error(JSON.stringify(responseJson));
    }

    const workbooks = responseJson.values;
    const blocks = responseJson.layout.blocks;

    for (const [uuid, answers] of Object.entries(workbooks)) {
      const breakoutRoomId = resourceUuidToBreakoutRoomIdMap.get(uuid);
      if (breakoutRoomId) {
        dispatch(
          setWorkbook({
            uuid,
            breakoutRoomId,
            answers,
            flavor,
          }),
        );
      }
    }

    blocks.forEach(
      (block: {
        uuid: string;
        block_components: WorkbookComponentType[];
        type: WorkbookQuestionTypeType;
      }) => {
        const blockUuid = block.uuid;
        const blockComponents = block.block_components;

        // Don't do anything with instructions or read-only code blocks
        if (
          block.type === WorkbookQuestionTypeType.Instructions ||
          (block.type === WorkbookQuestionTypeType.Code && blockComponents[0].readonly)
        ) {
          return;
        }

        let question = "";
        if (block.type === WorkbookQuestionTypeType.Code) {
          question = blockComponents[0].title;
        } else {
          const questionComponent = blockComponents.find(
            (component: WorkbookComponentType) => component.type === WorkbookComponentTypeType.Text,
          );
          if (questionComponent) {
            question = (questionComponent.rich_text ?? questionComponent.value) || "";
          }
        }

        const multipleChoiceData: WorkbookQuestionMultipleChoiceDataType[] = [];
        if (block.type === WorkbookQuestionTypeType.MultipleChoice) {
          const choicesComponent = blockComponents.find(
            (component: WorkbookComponentType) =>
              component.type === WorkbookComponentTypeType.ChoicesField,
          );
          const choices = choicesComponent?.choices || [];
          choices.forEach((choice: { value: string; label: string }, index: number) => {
            multipleChoiceData.push({
              index,
              label: `${String.fromCharCode("A".charCodeAt(0) + index)}. ${choice.label}`,
              value: choice.value,
            });
          });
        }

        dispatch(
          setQuestion({
            uuid: blockUuid,
            type: block.type,
            components: blockComponents,
            multipleChoiceData,
            question,
            flavor,
          }),
        );
      },
    );

    return responseJson;
  } catch (e) {
    window.flightLogger.error("[getWorkbookDataHelper] getWorkbookData failed", e);
    throw e;
  }
}

export async function disableCaptionsTrackingHelper(resourceUuid: string) {
  window.flightLogger.log(`Enter disableCaptionsTrackingHelper: resourceUuid=${resourceUuid}`);
  return disableCaptionsTracking(resourceUuid);
}

export async function enableCaptionsTrackingHelper(resourceUuid: string) {
  window.flightLogger.log(`Enter enableCaptionsTrackingHelper: resourceUuid=${resourceUuid}`);
  try {
    const response = await enableCaptionsTracking(resourceUuid);

    if (!response.ok) {
      window.flightLogger.error(
        `[enableCaptionsTrackingHelper] enableCaptionsTrackingHelper failed: status=${response.status}, resourceUuid=${resourceUuid}`,
      );
      throw new Error("enableCaptionsTrackingHelper failed");
    }
  } catch (err) {
    window.flightLogger.error(
      `[enableCaptionsTrackingHelper] enableCaptionsTrackingHelper failed: resourceUuid=${resourceUuid}`,
      err,
    );
    throw err;
  }
}

export async function getLatestCaptionHelper(resourceUuid: string) {
  try {
    const response = await getLatestCaption(resourceUuid);
    const responseJson = await response.json();

    if (!response.ok) {
      window.flightLogger.error(
        `[getLatestCaptionHelper] getLatestCaptionHelper failed: status=${response.status}, resourceUuid=${resourceUuid}`,
      );
      throw new Error(JSON.stringify(responseJson));
    }

    window.flightLogger.log(
      `[getLatestCaptionHelper] getLatestCaptionHelper: responseJson=${JSON.stringify(
        responseJson,
      )}, resourceUuid=${resourceUuid}`,
    );
    return responseJson;
  } catch (err) {
    window.flightLogger.error(
      `[getLatestCaptionHelper] getLatestCaptionHelper failed: resourceUuid=${resourceUuid}`,
      err,
    );
    throw err;
  }
}
